import { useNavigate } from 'react-router-dom';
import React, { useState } from 'react';
import { GridRenderCellParams } from '@mui/x-data-grid';
import { Icon, IconButton, ListItemIcon, Menu, MenuItem, Snackbar, Tooltip } from '@mui/material';

import { InfraPlan } from '../../../models/plan/plan';

import ProgressBar from '../../controls/ProgressBar';
import ErrorMessage from '../../controls/ErrorMessage';
import DialogBeoordelingAdd from '../dialogs/DialogBeoordelingAdd';

import { planManagementService } from '../../../services/plan-management.service';
import DialogStartSharingOrganisatie from '../dialogs/DialogStartSharingOrganisatie';
import { MijnOrganisatieListItem } from '../../../models/user/mijnOrganisatieListItem';
import { authService } from '../../../services/auth.service';


interface PlannenActionMenuProps {
  mijnOrganisatie: MijnOrganisatieListItem;
  params: GridRenderCellParams<InfraPlan>;
  refetchPlannen?: () => void; // Add refetchPlannen prop
}

const PlannenActionMenu = ({ 
  mijnOrganisatie,
  params,
  refetchPlannen, // Destructure refetchPlannen
}: PlannenActionMenuProps) => {
  const canKeyUser = authService.isKeyUser();
  const navigate = useNavigate();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  const [snackbarMessage, setSnackbarMessage] = useState<string | null>(null);
  const [open, setOpenDialogs] = useState({
    beoordeling: false,
    sharing: false,
    snackbar: false
  });

  const handleOpenMenu = (event: React.MouseEvent<HTMLElement>, id: string) => {
    setSelectedId(id);
    setAnchorEl(event.currentTarget);
  };

  const navigateToPlan = (planId: string) => {
    // plannen/${item.infplId}
  };

  const onClickSharePlan = (open: boolean = false) => {
    openDialog('sharing');
    handleCloseMenu();
  };

  const handleConfirmPlanSharing = async () => {
    closeDialog('sharing');
    setSnackbarMessage('Plan is gedeeld met organisatie. Het kan soms even duren voordat het overzicht is bijgewerkt.');
    openDialog('snackbar');

    if (refetchPlannen) {
      await refetchPlannen(); // Trigger refetch after submission
    }
  };

  const onClickBeoordeelPlan = (open: boolean = false) => {
    openDialog('beoordeling');
    handleCloseMenu();
  };

  const handleSubmitBeoordeling = async (beoordeling: any) => {
    try {
      setLoading(true);
      
      if (!selectedId) {
        setError('Geen planId gekozen om beoordeling aan toe te voegen');
        setLoading(false);
        return;  
      }

      const response = await planManagementService.addPlanBeoordeling(beoordeling, selectedId);
      if (response.ok) {
        closeDialog('beoordeling');
        setSnackbarMessage('Beoordeling is opgeslagen. Het kan soms even duren voordat het overzicht is bijgewerkt.');
        openDialog('snackbar');

        if (refetchPlannen) {
          await refetchPlannen(); // Trigger refetch after submission
        }
      }

      setLoading(false);
    }
    catch (error) {
      setLoading(false);
      console.error('Error updating beoordeling:', error);
      setError('Er is een fout opgetreden');
    }
  };

  // Handlers to open/close each dialog
  const openDialog = (dialog: keyof typeof open) => {
    // setSelectedId(id);
    setOpenDialogs((prev) => ({ ...prev, [dialog]: true }));
  };

  const closeDialog = (dialog: keyof typeof open) => {
    setOpenDialogs((prev) => ({ ...prev, [dialog]: false }));
  };

  const handleCloseMenu = () => {
    setAnchorEl(null);
    // setSelectedId(null);
  };

  return (
    <>
      <Snackbar
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
        open={open.snackbar}
        autoHideDuration={2000}
        onClose={() => closeDialog('snackbar')}
        message={snackbarMessage ? snackbarMessage : 'Er is een fout opgetreden'}
        key={'topcenter'}
      />

      <IconButton onClick={(e) => handleOpenMenu(e, params.id as string)}>
        <Tooltip title="Acties" placement="right" arrow>
          <Icon>more_vert</Icon>
        </Tooltip>
      </IconButton>
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleCloseMenu}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        {params && params.row?.infplBeoordeling && (
          <MenuItem onClick={() => onClickBeoordeelPlan(true)}>
            <ListItemIcon>
              <Icon>edit</Icon>
            </ListItemIcon>
            Beoordeling wijzigen
          </MenuItem>
        )}

        { mijnOrganisatie && 
          params && 
          params.row?.infplOrganizationId && 
          params.row?.infplOrganizationId === mijnOrganisatie.id && 
          canKeyUser && (
          <MenuItem onClick={() => onClickSharePlan()}>
            <ListItemIcon>
              <Icon>share</Icon>
            </ListItemIcon>
            Plan delen met organisatie
          </MenuItem>
        )}
      </Menu>

      { loading && (
        <ProgressBar />
      )}

      { error && (
        <ErrorMessage message={error} />
      )}

      <DialogStartSharingOrganisatie
        open={open.sharing}
        title="Plan delen met organisatie"
        contentText="Kies een van de beschikbare organisaties waarmee u uw plan wilt delen en druk op Start met delen."
        sharing="plan"
        groupBySource
        planId={params.row?.infplId || undefined}
        onClose={() => closeDialog('sharing')}
        onSubmit={handleConfirmPlanSharing}/>

      <DialogBeoordelingAdd
        open={open.beoordeling}
        planNaam={params.row?.planNaam ? params.row.planNaam : ''}
        onClose={ () => closeDialog('beoordeling') }
        onSubmit={handleSubmitBeoordeling}/>
    </>
  );
};

export default PlannenActionMenu;
